import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, RobotOutlined } from '@ant-design/icons';
import API_BASE_URL from '../../helpers/api/apiConfig';
import type { AreaTypeItem, CompanyOption } from './types';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

type AgentContextItem = {
  id: number;
  name: string;
  companyId?: number | null;
  isGlobal: boolean;
  isActive: boolean;
  areaTypeId?: number | null;
  company?: { id: number; name: string } | null;
  areaType?: { id: number; name: string } | null;
  systemPrompt?: string | null;
  contextData?: string | null;
  createdAt?: string;
};

interface AgentContextsTabProps {
  isMaster: boolean;
  companyId?: number;
  buildAuthHeaders: (includeContentType?: boolean) => Record<string, string>;
  onAuthMismatch: (response: Response, errorData: any) => Promise<boolean>;
}

const AgentContextsTab: React.FC<AgentContextsTabProps> = ({
  isMaster,
  companyId,
  buildAuthHeaders,
  onAuthMismatch,
}) => {
  const [contexts, setContexts] = useState<AgentContextItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<AgentContextItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [areaTypes, setAreaTypes] = useState<AreaTypeItem[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [form] = Form.useForm();
  const formIsGlobal = Form.useWatch('isGlobal', form);

  const canSelectCompany = isMaster;

  const fetchContexts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '200');
      params.set('offset', '0');
      params.set('is_active', showInactive ? 'false' : 'true');
      const response = await fetch(`${API_BASE_URL}/agents/contexts?${params.toString()}`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setContexts(data?.data || []);
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to load agents');
      }
    } catch (error) {
      console.error('Error fetching agents', error);
      message.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const fetchAreaTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/area-types`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAreaTypes(data?.data || []);
      }
    } catch (error) {
      console.warn('Failed to load area types for agents', error);
    }
  };

  const fetchCompanies = async () => {
    if (!isMaster) return;
    try {
      const response = await fetch(`${API_BASE_URL}/companies?scope=all`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data || []);
      }
    } catch (error) {
      console.warn('Failed to load companies for agents', error);
    }
  };

  useEffect(() => {
    fetchContexts();
  }, [showInactive]);

  useEffect(() => {
    fetchAreaTypes();
    fetchCompanies();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      isGlobal: false,
      isActive: true,
      companyId: !isMaster ? companyId : companyId || undefined,
    });
    setModalOpen(true);
  };

  const openEdit = async (record: AgentContextItem) => {
    setEditing(record);
    setModalOpen(true);
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/agents/contexts/${record.id}`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const context = data?.data || data;
        form.setFieldsValue({
          name: context.name,
          systemPrompt: context.systemPrompt,
          contextData: context.contextData,
          areaTypeId: context.areaTypeId || undefined,
          isGlobal: !!context.isGlobal,
          companyId: context.companyId || undefined,
          isActive: context.isActive,
        });
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to load agent details');
      }
    } catch (error) {
      console.error('Failed to load agent details', error);
      message.error('Failed to load agent details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload: any = {
        name: values.name,
        systemPrompt: values.systemPrompt,
        contextData: values.contextData || null,
        areaTypeId: values.areaTypeId || null,
        isGlobal: !!values.isGlobal,
        isActive: values.isActive !== undefined ? values.isActive : true,
      };

      if (!payload.isGlobal) {
        payload.companyId = canSelectCompany ? values.companyId || companyId : companyId;
        if (!payload.companyId) {
          message.error('Company is required for non-global agents');
          setSaving(false);
          return;
        }
      } else {
        payload.companyId = null;
      }

      const response = await fetch(
        editing
          ? `${API_BASE_URL}/agents/contexts/${editing.id}`
          : `${API_BASE_URL}/agents/contexts`,
        {
          method: editing ? 'PUT' : 'POST',
          headers: {
            ...buildAuthHeaders(),
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );

      if (response.ok || response.status === 204) {
        message.success(editing ? 'Agent updated' : 'Agent created');
        setModalOpen(false);
        setEditing(null);
        await fetchContexts();
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to save agent');
      }
    } catch (error) {
      if ((error as any)?.errorFields) return;
      console.error('Failed to save agent', error);
      message.error('Failed to save agent');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (record: AgentContextItem, nextValue: boolean) => {
    try {
      const response = await fetch(`${API_BASE_URL}/agents/contexts/${record.id}`, {
        method: 'PUT',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ isActive: nextValue }),
      });

      if (response.ok || response.status === 204) {
        setContexts((prev) =>
          prev.map((item) => (item.id === record.id ? { ...item, isActive: nextValue } : item))
        );
        message.success(nextValue ? 'Agent activated' : 'Agent deactivated');
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to update agent status');
      }
    } catch (error) {
      console.error('Failed to update agent status', error);
      message.error('Failed to update agent status');
    }
  };

  const handleDeactivate = async (record: AgentContextItem) => {
    try {
      const response = await fetch(`${API_BASE_URL}/agents/contexts/${record.id}`, {
        method: 'DELETE',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok || response.status === 204) {
        setContexts((prev) =>
          prev.map((item) => (item.id === record.id ? { ...item, isActive: false } : item))
        );
        message.success('Agent deactivated');
      } else {
        const errorData = await response.json();
        if (await onAuthMismatch(response, errorData)) return;
        message.error(errorData?.message || 'Failed to deactivate agent');
      }
    } catch (error) {
      console.error('Failed to deactivate agent', error);
      message.error('Failed to deactivate agent');
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Scope',
        key: 'scope',
        render: (_: any, record: AgentContextItem) =>
          record.isGlobal ? <Tag color="gold">Global</Tag> : <Tag color="blue">Company</Tag>,
      },
      {
        title: 'Company',
        key: 'company',
        render: (_: any, record: AgentContextItem) =>
          record.isGlobal ? '—' : record.company?.name || '—',
      },
      {
        title: 'Area Type',
        key: 'areaType',
        render: (_: any, record: AgentContextItem) => record.areaType?.name || '—',
      },
      {
        title: 'Status',
        key: 'status',
        render: (_: any, record: AgentContextItem) => (
          <Space>
            <Switch
              size="small"
              checked={record.isActive}
              onChange={(checked) => handleToggleActive(record, checked)}
            />
            <Text type={record.isActive ? 'success' : 'secondary'}>
              {record.isActive ? 'Active' : 'Inactive'}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_: any, record: AgentContextItem) => (
          <Space>
            <Button type="link" onClick={() => openEdit(record)}>
              Edit
            </Button>
            {record.isActive && (
              <Popconfirm
                title="Deactivate agent?"
                description="This will keep the agent but stop it from being selected."
                okText="Deactivate"
                okType="danger"
                cancelText="Cancel"
                onConfirm={() => handleDeactivate(record)}
              >
                <Button type="link" danger>
                  Deactivate
                </Button>
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [contexts]
  );

  return (
    <Card
      title={
        <Space>
          <RobotOutlined />
          AI Agents
        </Space>
      }
      bordered={false}
      style={{ background: '#fff' }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Paragraph>
          Create and manage AI agent profiles, prompts, and optional area-type scoping.
        </Paragraph>
        {!companyId && !isMaster && (
          <Alert
            type="warning"
            message="No active company"
            description="Select a company before creating agents."
            showIcon
          />
        )}
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New Agent
          </Button>
          <Space>
            <Text type="secondary">Show inactive</Text>
            <Switch checked={showInactive} onChange={setShowInactive} />
          </Space>
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={contexts}
          pagination={{ pageSize: 10 }}
        />
      </Space>

      <Modal
        title={editing ? 'Edit Agent' : 'New Agent'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onOk={handleSave}
        okText={editing ? 'Save' : 'Create'}
        confirmLoading={saving}
        width={720}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          disabled={detailLoading}
          initialValues={{ isGlobal: false, isActive: true }}
        >
          <Form.Item
            label="Agent name"
            name="name"
            rules={[{ required: true, message: 'Please enter an agent name' }]}
          >
            <Input placeholder="e.g. Turf Health Analyst" />
          </Form.Item>

          {canSelectCompany && (
            <>
              <Form.Item label="Global agent" name="isGlobal" valuePropName="checked">
                <Switch />
              </Form.Item>
              {!formIsGlobal && (
                <Form.Item
                  label="Company"
                  name="companyId"
                  rules={[{ required: true, message: 'Select a company for this agent' }]}
                >
                  <Select placeholder="Select company" allowClear>
                    {companies.map((company) => (
                      <Select.Option key={company.id} value={company.id}>
                        {company.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </>
          )}

          {!canSelectCompany && (
            <Form.Item label="Company">
              <Text>{companyId ? `Company #${companyId}` : 'No company selected'}</Text>
            </Form.Item>
          )}

          <Form.Item label="Area type (optional)" name="areaTypeId">
            <Select placeholder="Applies to any area type" allowClear>
              {areaTypes.map((areaType) => (
                <Select.Option key={areaType.id} value={areaType.id}>
                  {areaType.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="System prompt"
            name="systemPrompt"
            rules={[{ required: true, message: 'Please add a system prompt' }]}
          >
            <TextArea rows={6} placeholder="Describe how the agent should behave and respond." />
          </Form.Item>

          <Form.Item label="Context data (optional)" name="contextData">
            <TextArea rows={4} placeholder="Extra context for this agent (data sources, constraints, tone)." />
          </Form.Item>

          {editing && (
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
};

export default AgentContextsTab;
